import type { NotificationListItem } from '@invoice/shared/schemas';

import type { useI18n } from './i18n';

type Translate = ReturnType<typeof useI18n>['t'];

export interface NotificationSummary {
  title: string;
  message: string;
  level: 'info' | 'warning' | 'error';
  invoiceId: string | null;
  meta: string | null;
}

export function summariseNotification(
  item: NotificationListItem,
  t: Translate,
): NotificationSummary {
  const payload = item.payload;
  const invoiceId = stringValue(payload.invoiceId) ?? null;
  const invoiceLabel = invoiceId ? `#${invoiceId}` : 'Invoice';

  switch (item.type) {
    case 'invoice_updated': {
      const actor = stringValue(payload.actorEmail) ?? t('notifications.someone');
      return {
        title: t('notifications.invoiceUpdatedTitle'),
        message: t('notifications.invoiceUpdatedMessage', { actor, invoice: invoiceLabel }),
        level: 'info',
        invoiceId,
        meta: stringValue(payload.filename) ?? null,
      };
    }
    case 'invoice_comment_created': {
      const actor = stringValue(payload.actorEmail) ?? t('notifications.someone');
      return {
        title: t('notifications.commentCreatedTitle'),
        message: t('notifications.commentCreatedMessage', { actor, invoice: invoiceLabel }),
        level: 'info',
        invoiceId,
        meta: stringValue(payload.body) ?? null,
      };
    }
    case 'invoice_status_changed': {
      const actor = stringValue(payload.actorEmail) ?? t('notifications.someone');
      const note = stringValue(payload.note);
      return {
        title: t('notifications.statusChangedTitle'),
        message: `${t('notifications.statusChangedMessage', {
          actor,
          from: localiseInvoiceStatus(stringValue(payload.previousStatus), t),
          to: localiseInvoiceStatus(stringValue(payload.nextStatus), t),
        })}${note ? ` ${note}` : ''}`,
        level: 'info',
        invoiceId,
        meta: stringValue(payload.filename) ?? null,
      };
    }
    case 'invoice_manual_review':
      return {
        title: t('notifications.manualReviewTitle'),
        message: t('notifications.manualReviewMessage', {
          invoice: invoiceLabel,
          details: joinList(payload.reasons) ?? '',
        }),
        level: 'warning',
        invoiceId,
        meta: stringValue(payload.filename) ?? null,
      };
    case 'invoice_failed':
      return {
        title: t('notifications.failedTitle'),
        message: t('notifications.failedMessage', {
          invoice: invoiceLabel,
          details: stringValue(payload.errorMessage) ?? '',
        }),
        level: 'error',
        invoiceId,
        meta: stringValue(payload.filename) ?? null,
      };
    case 'invoice_skonto_reminder':
      return {
        title: t('notifications.skontoReminderTitle'),
        message: t('notifications.skontoReminderMessage', {
          invoice: stringValue(payload.invoiceNumber) ?? invoiceLabel,
          deadline: stringValue(payload.skontoDeadline) ?? '-',
        }),
        level: 'warning',
        invoiceId,
        meta: stringValue(payload.filename) ?? null,
      };
    default:
      return {
        title: item.type,
        message: invoiceLabel,
        level: 'info',
        invoiceId,
        meta: null,
      };
  }
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function joinList(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  const items = value.filter(
    (entry): entry is string => typeof entry === 'string' && entry.length > 0,
  );
  return items.length > 0 ? items.join(', ') : null;
}

function localiseInvoiceStatus(status: string | null, t: Translate): string {
  switch (status) {
    case 'posted':
      return t('common.invoiceStatus.posted');
    case 'success':
      return t('common.invoiceStatus.success');
    case 'manual_review':
      return t('common.invoiceStatus.manual_review');
    case 'error':
      return t('common.invoiceStatus.error');
    case 'processing':
      return t('common.invoiceStatus.processing');
    case 'queued':
      return t('common.invoiceStatus.queued');
    default:
      return status ?? '-';
  }
}
